import { Skeleton, Switch } from "@mui/material";
import clsx from "clsx";
import type { IVerificaRecursosDaTelaResponse } from "@/api/space/dtos/authenticator.dto";
import {
	derivarVisaoConteudoRecurso,
	type PermissaoVisivel,
	type VisaoConteudoRecurso,
} from "../restricoesRecursoModal.utils";

// --------------------------------- TYPES ---------------------------------
export type ConteudoRestricoesProps = {
	readonly edicaoBloqueada: boolean;
	readonly carregando: boolean;
	readonly erro: boolean;
	readonly data: IVerificaRecursosDaTelaResponse | undefined;
	readonly estado: Map<string, boolean>;
	readonly isMesmoGrupo: boolean;
	readonly buscaPermissao: string;
	readonly somenteAtivas: boolean;
	readonly onAlternar: (tag: string) => void;
	readonly onDefinirPermissoes: (tags: string[], ativo: boolean) => void;
};

// ------------------------------- COMPONENTS ------------------------------
function CabecalhoRecurso({
	descricao,
	permissoesAtivas,
	totalPermissoes,
}: Readonly<{
	readonly descricao: string;
	readonly permissoesAtivas: number;
	readonly totalPermissoes: number;
}>) {
	return (
		<div className="sticky top-0 z-10 flex items-center gap-3.5 border-b bg-accent-foreground px-4 py-2">
			<span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-primary" />
			<span className="min-w-0 flex-1 truncate text-xs font-bold uppercase text-foreground sm:text-sm">
				{descricao}
			</span>
			<span
				className={clsx(
					"shrink-0 whitespace-nowrap rounded-full px-2 text-xs font-extrabold",
					permissoesAtivas > 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
				)}
			>
				{permissoesAtivas}/{totalPermissoes}
			</span>
		</div>
	);
}

function PermissaoItem({
	permissao,
	disabled,
	onAlternar,
}: Readonly<{
	readonly permissao: PermissaoVisivel;
	readonly disabled: boolean;
	readonly onAlternar: (tag: string) => void;
}>) {
	return (
		<label
			className={clsx(
				"flex select-none items-center gap-2 rounded-lg px-2 py-1.5",
				disabled ? "cursor-not-allowed" : "cursor-pointer hover:bg-muted/50",
			)}
		>
			<Switch
				checked={permissao.ativo}
				onChange={() => onAlternar(permissao.tag)}
				size="small"
				disabled={disabled}
			/>
			<span
				className={clsx(
					"text-sm font-semibold leading-tight",
					permissao.ativo ? "text-foreground" : "text-muted-foreground",
				)}
			>
				{permissao.descricao}
			</span>
		</label>
	);
}

function CorpoRecurso({
	edicaoBloqueada,
	recursoTag,
	visao,
	onAlternar,
	onDefinirPermissoes,
}: Readonly<{
	readonly edicaoBloqueada: boolean;
	readonly recursoTag: string;
	readonly visao: VisaoConteudoRecurso;
	readonly onAlternar: (tag: string) => void;
	readonly onDefinirPermissoes: (tags: string[], ativo: boolean) => void;
}>) {
	const {
		recursoAtivo,
		acessoTelaVisivel,
		totalPermissoes,
		permissoesAtivas,
		todasPermissoesAtivas,
		idsPermissoes,
		permissoesVisiveis,
	} = visao;
	const desabilitado = edicaoBloqueada || !acessoTelaVisivel;

	return (
		<div className="flex flex-col px-3 py-3 md:px-5">
			<div
				aria-disabled={desabilitado}
				className={clsx(
					"flex flex-col gap-2 md:flex-row md:items-start md:gap-0",
					desabilitado ? "cursor-not-allowed opacity-80 **:cursor-not-allowed" : undefined,
				)}
			>
				<div className="flex w-full shrink-0 flex-col gap-1.5 md:w-xs md:pr-4">
					<label
						className={clsx(
							"flex select-none items-center gap-2",
							desabilitado ? "cursor-not-allowed" : "cursor-pointer",
						)}
					>
						<Switch
							checked={recursoAtivo}
							onChange={() => onAlternar(recursoTag)}
							size="small"
							title="Acesso à tela"
							disabled={desabilitado}
						/>

						<span
							className={clsx(
								"text-md font-bold leading-tight",
								recursoAtivo ? "text-foreground" : "text-muted-foreground",
							)}
						>
							Acesso à tela
						</span>
					</label>

					{totalPermissoes > 0 ? (
						<div className={clsx("flex items-center gap-2", acessoTelaVisivel && "pl-9")}>
							<span
								className={clsx(
									"shrink-0 whitespace-nowrap rounded-full px-2 text-xs font-extrabold",
									permissoesAtivas > 0
										? "bg-primary/10 text-primary"
										: "bg-muted text-muted-foreground",
								)}
							>
								{permissoesAtivas}/{totalPermissoes}
							</span>

							<button
								type="button"
								onClick={() => onDefinirPermissoes(idsPermissoes, !todasPermissoesAtivas)}
								disabled={desabilitado}
								className={clsx(
									"text-xs text-primary",
									"enabled:hover:cursor-pointer enabled:hover:underline",
								)}
							>
								{todasPermissoesAtivas ? "Limpar permissões" : "Marcar todas as permissões"}
							</button>
						</div>
					) : null}
				</div>

				{totalPermissoes > 0 ? (
					<div className="grid flex-1 content-start gap-x-1 gap-y-0.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{permissoesVisiveis.map((permissao) => (
							<PermissaoItem
								key={permissao.tag}
								permissao={permissao}
								disabled={desabilitado}
								onAlternar={onAlternar}
							/>
						))}
					</div>
				) : (
					<p className="flex-1 px-2 py-4 text-center text-sm font-semibold text-muted-foreground">
						Esta tela não possui permissões configuráveis.
					</p>
				)}
			</div>
		</div>
	);
}

function ConteudoVazio() {
	return (
		<div className="px-5 py-16 text-center">
			<p className="text-md font-bold text-foreground">Nenhuma permissão encontrada</p>
			<p className="mt-1.5 text-sm text-muted-foreground">
				Ajuste a busca ou desative o filtro “Somente ativas”.
			</p>
		</div>
	);
}

// ------------------------------- COMPONENT -------------------------------
export default function ConteudoRestricoes({
	edicaoBloqueada,
	carregando,
	erro,
	data,
	estado,
	onAlternar,
	onDefinirPermissoes,
	isMesmoGrupo,
	buscaPermissao,
	somenteAtivas,
}: Readonly<ConteudoRestricoesProps>) {
	// -------------------------------- RETURN --------------------------------
	if (carregando)
		return (
			<div aria-busy className="mb-4 overflow-hidden rounded-2xl border">
				<Skeleton variant="rectangular" height={40} />
				<div className="px-3 py-3 md:px-5">
					<Skeleton variant="rounded" height={64} />
				</div>
			</div>
		);

	if (erro)
		return (
			<p className="px-2 py-10 text-center text-sm font-semibold text-danger">
				Não foi possível carregar as permissões desta tela.
			</p>
		);

	if (!data) return null;

	const visao = derivarVisaoConteudoRecurso({
		data,
		estado,
		buscaPermissao,
		somenteAtivas,
		isMesmoGrupo,
	});

	return (
		<section className="mb-4 overflow-hidden rounded-2xl border shadow-sm">
			<CabecalhoRecurso
				descricao={data.recurso.descricao}
				permissoesAtivas={visao.permissoesAtivas}
				totalPermissoes={visao.totalPermissoes}
			/>

			{visao.temConteudoVisivel ? (
				<CorpoRecurso
					edicaoBloqueada={edicaoBloqueada}
					recursoTag={data.recurso.tag}
					visao={visao}
					onAlternar={onAlternar}
					onDefinirPermissoes={onDefinirPermissoes}
				/>
			) : (
				<ConteudoVazio />
			)}
		</section>
	);
}
